import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Grid, Paper, Typography,
  TextField, MenuItem,
  Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow,
  TablePagination, TableSortLabel,
  Collapse
} from '@mui/material';
import {
  format,
  startOfToday,
  endOfToday,
  startOfDay,
  endOfDay,
  subDays
} from 'date-fns';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { retrieveAllAuditLogsApi } from '../../../components/api/AuditLogApiService';
import ShippingInstructionClosingExport from '../../../components/Export/ShippingInstructionClosingExport';
import { retrieveUsersApi }        from '../../../components/api/UserApiService';

const descendingComparator = (a, b, key) => (b[key] < a[key] ? -1 : b[key] > a[key] ? 1 : 0);
const getComparator = (order, key) =>
  order === 'desc'
    ? (a, b) => descendingComparator(a, b, key)
    : (a, b) => -descendingComparator(a, b, key);
const stableSort = (arr, comp) => {
  const stabilized = arr.map((el, ix) => [el, ix]);
  stabilized.sort((a, b) => {
    const order = comp(a[0], b[0]);
    return order !== 0 ? order : a[1] - b[1];
  });
  return stabilized.map(el => el[0]);
};

const headCells = [
  { id: 'id',         label: 'ID' },
  { id: 'entityType', label: 'Entity Type' },
  { id: 'entityId',   label: 'Entity ID' },
  { id: 'username',   label: 'User Name' },
  { id: 'modifiedAt', label: 'Modified At (Turkish Time)' },
  { id: 'details',    label: '' }
];

export default function AuditLogView() {
  const [filters, setFilters] = useState({
    entityType:   '',
    entityId:     '',
    username:     '',
    fromDateTime: '',
    toDateTime:   ''
  });
  const [users, setUsers]       = useState([]);
  const [rows, setRows]         = useState([]);
  const [order, setOrder]       = useState('desc');
  const [orderBy, setOrderBy]   = useState('modifiedAt');
  const [page, setPage]         = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [expandedIds, setExpandedIds] = useState([]);

  const loadLogs = () => {
    retrieveAllAuditLogsApi(filters)
      .then(res => setRows(res.data))
      .catch(console.error);
  };

  useEffect(() => {
    loadLogs();
  }, []);

  const applyPreset = (range) => {
    let from, to = new Date();
    switch(range) {
      case 'today':
        from = startOfToday(); to = endOfToday(); break;
      case 'yesterday':
        from = startOfDay(subDays(new Date(),1)); to = endOfDay(subDays(new Date(),1)); break;
      case '7days':
        from = startOfDay(subDays(new Date(),7)); break;
      case '30days':
        from = startOfDay(subDays(new Date(),30)); break;
      default: return;
    }
    setFilters(f => ({
      ...f,
      fromDateTime: format(from, "yyyy-MM-dd'T'HH:mm"),
      toDateTime:   format(to,   "yyyy-MM-dd'T'HH:mm")
    }));
    setPage(0);
    setTimeout(loadLogs, 0);
  };

  const handleUsersOpen = () => {
    if (users.length === 0) {
      retrieveUsersApi()
        .then(res => setUsers(res.data))
        .catch(console.error);
    }
  };

  const handleSearch = () => { setPage(0); loadLogs(); };
  const handleClear  = () => {
    setFilters({ entityType:'', entityId:'', username:'', fromDateTime:'', toDateTime:'' });
    setRows([]);
  };
  const handleFilterChange = (field, val) => setFilters(f => ({...f, [field]:val}));
  const handleRequestSort  = (_e, prop) => {
    const isAsc = orderBy===prop && order==='asc';
    setOrder(isAsc?'desc':'asc'); setOrderBy(prop);
  };
  const handleChangePage = (_e,newPage) => setPage(newPage);
  const handleChangeRowsPerPage = e => { setRowsPerPage(+e.target.value); setPage(0); };
  const handleToggle = id => setExpandedIds(prev => prev.includes(id)?prev.filter(x=>x!==id):[...prev,id]);

  const visibleRows = useMemo(() =>
    stableSort(rows, getComparator(order, orderBy))
      .slice(page*rowsPerPage,page*rowsPerPage+rowsPerPage)
  ,[rows,order,orderBy,page,rowsPerPage]);

  const entityTypes = ['MENUITEM','OFFER','ORDER','SHIPMENTREQUEST','SHIPPINGINSTRUCTION','QUANTITY','TRAVELCALENDAR'];

  return (
    <Box sx={{ width:'100%',p:2 }}>
      <Typography variant="h6" gutterBottom>Audit Logs</Typography>

      <Box mb={2}>
        {['today','yesterday','7days','30days'].map(lbl=>(
          <Button key={lbl} size="small" onClick={()=>applyPreset(lbl)} sx={{mr:1}}>
            {lbl==='7days'?'Last 7 Days':lbl==='30days'?'Last 30 Days':lbl.charAt(0).toUpperCase()+lbl.slice(1)}
          </Button>
        ))}
      </Box>

      <Box component="form" mb={2}>
        <Grid container spacing={2} alignItems="center">
          <Grid item sx={{ minWidth: 140 }}>
            <TextField label="Entity Type" select size="small" fullWidth
              value={filters.entityType}
              onChange={e=>handleFilterChange('entityType',e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              {entityTypes.map(t=><MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
          </Grid>
          
          <Grid item>
            <TextField label="Entity ID" type="number" size="small"
              value={filters.entityId}
              onChange={e=>handleFilterChange('entityId',e.target.value)}
            />
          </Grid>
          <Grid item sx={{ minWidth: 140 }}>
            <TextField label="User" select size="small" fullWidth 
              value={filters.username}
              onChange={e=>handleFilterChange('username',e.target.value)}
              SelectProps={{onOpen:handleUsersOpen}}
            >
              <MenuItem value="">All</MenuItem>
              {users.map(u=><MenuItem key={u.id} value={u.email}>{u.name}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item>
            <TextField label="From (Turkish Time)" type="datetime-local" size="small"
              InputLabelProps={{shrink:true}}
              value={filters.fromDateTime}
              onChange={e=>handleFilterChange('fromDateTime',e.target.value)}
            />
          </Grid>
          <Grid item>
            <TextField label="To (Turkish Time)" type="datetime-local" size="small"
              InputLabelProps={{shrink:true}}
              value={filters.toDateTime}
              onChange={e=>handleFilterChange('toDateTime',e.target.value)}
            />
          </Grid>
          <Grid item>
            <Button variant="contained" size="small" onClick={handleSearch}>Search</Button>
          </Grid>
          <Grid item>
            <Button variant="outlined" size="small" onClick={handleClear}>Clear</Button>
          </Grid>
           <Grid item>
            <ShippingInstructionClosingExport />
          </Grid>
        </Grid>
      </Box>

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {headCells.map(c=>(
                  <TableCell key={c.id} align="center">
                    {c.id!=="details" && (
                      <TableSortLabel
                        active={orderBy===c.id}
                        direction={orderBy===c.id?order:'asc'}
                        onClick={e=>handleRequestSort(e,c.id)}
                      >{c.label}</TableSortLabel>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.map(r=>(
                <React.Fragment key={r.id}>
                  <TableRow hover onClick={()=>handleToggle(r.id)} sx={{cursor:'pointer'}}>
                    <TableCell align="center">{r.id}</TableCell>
                    <TableCell align="center">{r.entityType}</TableCell>
                    <TableCell align="center">{r.entityId}</TableCell>
                    <TableCell align="center">{r.username}</TableCell>
                    <TableCell align="center">{format(new Date(r.modifiedAt),'dd/MM/yyyy HH:mm')}</TableCell>
                    <TableCell align="center">{expandedIds.includes(r.id)?<ExpandLessIcon/>:<ExpandMoreIcon/>}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={6} sx={{p:0}}>
                      <Collapse in={expandedIds.includes(r.id)} timeout="auto" unmountOnExit>
                        <Box sx={{p:2}} dangerouslySetInnerHTML={{__html:r.changes}}/>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5,10,25,50]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
}
